/** Persist family pack owner credentials on this device (survives browser restart). */

export interface SavedAdhocPack {
  adhocId: string;
  packCode: string;
  ownerToken: string;
  savedAt: string;
}

export interface PendingPackCheckout {
  adhocId: string;
  checkoutId: string;
  provider: 'stripe' | 'hitpay';
  savedAt: string;
}

export interface PendingGuestCheckout extends PendingPackCheckout {
  sessionId: string;
}

function storageEnvSuffix(): '_dev' | '_prod' {
  const backend = process.env.BACKEND_URL || '';
  return backend.includes('/dev/') || backend.endsWith('/dev/api') ? '_dev' : '_prod';
}

const PACK_KEY = `adhoc_family_pack_v2${storageEnvSuffix()}`;
const PENDING_KEY = `adhoc_pending_pack_checkout${storageEnvSuffix()}`;
const PENDING_GUEST_KEY = `adhoc_pending_guest_checkout${storageEnvSuffix()}`;
const PENDING_CREDITS_KEY = `adhoc_pending_credits_checkout${storageEnvSuffix()}`;
const CREDITS_POOL_KEY = `adhoc_credits_pool${storageEnvSuffix()}`;
const LEGACY_PACK_KEY = 'adhoc_family_pack_v2';

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private mode */
  }
}

function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function migrateLegacyPack(): SavedAdhocPack | null {
  const legacy = readJson<SavedAdhocPack>(LEGACY_PACK_KEY);
  if (!legacy?.adhocId || !legacy?.packCode || !legacy?.ownerToken) return null;
  writeJson(PACK_KEY, legacy);
  removeKey(LEGACY_PACK_KEY);
  removeKey('adhoc_family_pack_v1');
  return legacy;
}

export function readSavedAdhocPack(): SavedAdhocPack | null {
  const saved = readJson<SavedAdhocPack>(PACK_KEY) ?? migrateLegacyPack();
  if (!saved?.adhocId || !saved?.packCode || !saved?.ownerToken) return null;
  return saved;
}

export function saveAdhocPack(adhocId: string, packCode: string, ownerToken: string): void {
  const payload: SavedAdhocPack = {
    adhocId,
    packCode,
    ownerToken,
    savedAt: new Date().toISOString(),
  };
  writeJson(PACK_KEY, payload);
  removeKey(LEGACY_PACK_KEY);
}

export function clearSavedAdhocPack(): void {
  removeKey(PACK_KEY);
  removeKey(LEGACY_PACK_KEY);
  removeKey('adhoc_family_pack_v1');
}

export function savePendingPackCheckout(
  adhocId: string,
  checkoutId: string,
  provider: 'stripe' | 'hitpay',
): void {
  const payload: PendingPackCheckout = {
    adhocId,
    checkoutId,
    provider,
    savedAt: new Date().toISOString(),
  };
  writeJson(PENDING_KEY, payload);
}

export function readPendingPackCheckout(): PendingPackCheckout | null {
  const pending = readJson<PendingPackCheckout>(PENDING_KEY);
  if (!pending?.adhocId || !pending?.checkoutId || !pending?.provider) return null;
  return pending;
}

export function clearPendingPackCheckout(): void {
  removeKey(PENDING_KEY);
}

export interface SavedCreditsPool {
  adhocId: string;
  shareCode: string;
  savedAt: string;
}

export function saveCreditsPool(adhocId: string, shareCode: string): void {
  writeJson(CREDITS_POOL_KEY, {
    adhocId,
    shareCode,
    savedAt: new Date().toISOString(),
  } satisfies SavedCreditsPool);
}

export function readCreditsPool(): SavedCreditsPool | null {
  const saved = readJson<SavedCreditsPool>(CREDITS_POOL_KEY);
  if (!saved?.adhocId || !saved?.shareCode) return null;
  return saved;
}

export function clearCreditsPool(): void {
  removeKey(CREDITS_POOL_KEY);
}

export function savePendingCreditsCheckout(
  adhocId: string,
  checkoutId: string,
  provider: 'stripe' | 'hitpay',
): void {
  writeJson(PENDING_CREDITS_KEY, {
    adhocId,
    checkoutId,
    provider,
    savedAt: new Date().toISOString(),
  } satisfies PendingPackCheckout);
}

export function readPendingCreditsCheckout(): PendingPackCheckout | null {
  const pending = readJson<PendingPackCheckout>(PENDING_CREDITS_KEY);
  if (!pending?.adhocId || !pending?.checkoutId || !pending?.provider) return null;
  return pending;
}

export function clearPendingCreditsCheckout(): void {
  removeKey(PENDING_CREDITS_KEY);
}

export function savePendingGuestCheckout(
  adhocId: string,
  sessionId: string,
  checkoutId: string,
  provider: 'stripe' | 'hitpay',
): void {
  const payload: PendingGuestCheckout = {
    adhocId,
    sessionId,
    checkoutId,
    provider,
    savedAt: new Date().toISOString(),
  };
  writeJson(PENDING_GUEST_KEY, payload);
}

export function readPendingGuestCheckout(): PendingGuestCheckout | null {
  const pending = readJson<PendingGuestCheckout>(PENDING_GUEST_KEY);
  if (!pending?.adhocId || !pending?.sessionId || !pending?.checkoutId || !pending?.provider) {
    return null;
  }
  return pending;
}

export function clearPendingGuestCheckout(): void {
  removeKey(PENDING_GUEST_KEY);
}
